import { Action, ACTION_TYPE } from '@abiglobalhealth/go-types'
import * as Sentry from '@sentry/browser'
import axios, { AxiosError, AxiosHeaders, isAxiosError } from 'axios'

import { getApiKey } from '@/locale/utils'
import { isErrorIgnored, ResponseError } from '@/utils/errors'

const DEFAULT_SUBDOMAINS = ['goapp', 'localhost']

const regex = /^(dev-|test-|canary-)/
export const getEnvUrl = (variable: string) => {
  const region = window.location.hostname.split('.')[0].replace(regex, '')

  if (DEFAULT_SUBDOMAINS.includes(region)) return import.meta.env[variable]

  return import.meta.env[`${variable}_${region.toUpperCase()}`]
}

const abiApiHeaders = () =>
  new AxiosHeaders({
    'x-api-key': getApiKey(),
    'Content-Type': 'application/json'
  })

export const actionsRequest = <T extends Action<ACTION_TYPE>>(
  body: { payload: T['payload']; type: ACTION_TYPE; version?: string },
  signal?: AbortSignal
) =>
  axios
    .post<T['response']>(`${getEnvUrl('VITE_API_URL')}/flow`, body, {
      headers: abiApiHeaders(),
      signal
    })
    .catch((error) => {
      handleRequestError(error, body.type, body.payload)
      throw error
    })

export const handleRequestError = (error: unknown, action: ACTION_TYPE, payload?: unknown) => {
  const parsedErr = error as Error

  Sentry.withScope((scope) => {
    scope.setExtra('Error', typeof error === 'object' ? { ...error } : { error })
    scope.setLevel(isErrorIgnored(error) ? 'warning' : 'error')
    scope.setTag('action', action)
    scope.setExtra('Request', {
      action,
      payload
    })

    if (isAxiosError(parsedErr)) {
      const axiosError = parsedErr as AxiosError<ResponseError>

      const errorData = axiosError?.response?.data
      if (errorData) {
        scope.setExtra('Axios Error', errorData)
        parsedErr.message = `${errorData?.type} ${errorData?.message ? `- ${errorData?.message}` : ''}`
      }
      parsedErr.name = `${parsedErr.code} ${parsedErr.status ? parsedErr.status : ''} ${action ? `- ${action}` : ''}`
    } else {
      parsedErr.name = `${parsedErr.name}${action ? ` - ${action}` : ''}`
    }

    scope.captureException(error)
  })
}
